 

import { createI18n } from 'vue-i18n'
import type {
  DateTimeFormatKey,
  I18nKey,
  NumberFormatKey,
} from '@/locales/i18n.types'
import {
  datetimeFormats,
  numberFormats,
} from '@team-milva/lab-constants/i18nFormats'
import { ENV, HOST } from '@/globals/typescript/utils/host'
import { merge } from 'lodash-es'
import {
  LAB_ADMIN_LOCALES,
  type TLabAdminLocale,
} from '@team-milva/lab-models/language'

// Only warn once per key for the "missing everywhere" case
const warnedMissingEverywhere = new Set<string>()

export const i18n = createI18n({
  legacy: false as const,
  globalInjection: true as const,
  locale: 'en' as TLabAdminLocale,
  fallbackLocale: 'en' as TLabAdminLocale,

  missingWarn: false,
  fallbackWarn: false,

  numberFormats,
  datetimeFormats,

  missing: (locale, key) => {
    if (key.startsWith('W: ')) return key.replace(/^W:\s*/, '')

    if (ENV.isDev) {
      const existsSomewhere = LAB_ADMIN_LOCALES.some((l) =>
        i18n.global.te(key, l)
      )

      if (
        key.includes(' ') &&
        !existsSomewhere &&
        !warnedMissingEverywhere.has(key)
      ) {
        warnedMissingEverywhere.add(key)
        console.warn(`!! Remember to prefix temporary keys with "W: " (${key})`)
        return key
      }

      console.warn(`Not found '${key}' key in '${locale}' locale messages.`)
    }

    return key
  },
})

export const t = (key: I18nKey, params?: Record<string, unknown>) =>
  params === undefined ? i18n.global.t(key) : i18n.global.t(key, params)

export const n = (
  value: number,
  format?: NumberFormatKey | Intl.NumberFormatOptions,
  locale?: TLabAdminLocale
) =>
  locale !== undefined
    ? i18n.global.n(value, format as string, locale)
    : format !== undefined
      ? i18n.global.n(value, format as string)
      : i18n.global.n(value)

/**
 * Maps an app *content* locale onto the locale used for `Intl` *display*
 * formatting.
 *
 * The content locale ('en' / 'da') is not only a message-bundle key — it is
 * also an `ITextObj` key and a Firestore write key (`name.${locale}`), so it
 * can never carry a region subtag without forking the stored data.
 *
 * But bare 'en' resolves to en-US in ICU, which formats dates month-first
 * (`Feb 12, 2025`) — inconsistent with Danish (`12. feb. 2025`), and with the
 * day-first ordering the rest of the product uses. This map is the display-only
 * counterpart, keeping the two concerns separate.
 *
 * (The equivalent comment in lab-management additionally names that app's own
 * `Intl.DateTimeFormat(..., 'en-GB', ...)` call sites; there are none in this
 * repo, so they are deliberately not listed here.)
 *
 * Verified: 'da'/'da-DK' and 'de'/'de-DE' produce byte-identical output for
 * every format in `datetimeFormats` — they are listed for explicitness, and
 * only 'en' actually changes anything.
 *
 * NOTE: duplicated in lab-management's `src/locales/index.ts`. It belongs in
 * `@team-milva/lab-constants/i18nFormats` alongside `datetimeFormats`, but a
 * new export there cannot be consumed until that package is released — move it
 * on the next `lab-constants` release and delete both copies.
 */
const INTL_DISPLAY_LOCALES: Record<string, string> = {
  en: 'en-GB',
  da: 'da-DK',
  de: 'de-DE',
}

/** Falls through unchanged for any locale with no explicit display mapping. */
const resolveIntlLocale = (locale: string): string =>
  INTL_DISPLAY_LOCALES[locale] ?? locale

export const d = (
  value: Date | number,
  format: DateTimeFormatKey,
  localeOrOptions?: TLabAdminLocale | Intl.DateTimeFormatOptions,
  maybeOptions?: Intl.DateTimeFormatOptions
) => {
  // The *content* locale — an ITextObj/Firestore key, never region-tagged.
  const contentLocale =
    typeof localeOrOptions === 'string'
      ? localeOrOptions
      : (i18n.global.locale.value as TLabAdminLocale)

  const extraOptions =
    typeof localeOrOptions === 'object' ? localeOrOptions : maybeOptions

  // getDateTimeFormat() is keyed by *locale*, then by format name. Passing the
  // format name straight in returns {} and silently drops the whole format,
  // so resolve it in two steps.
  const formatOptions = (
    i18n.global.getDateTimeFormat(contentLocale) as unknown as
      | Record<string, Intl.DateTimeFormatOptions>
      | undefined
  )?.[format]

  // Resolved options + an explicit display locale, so Intl gets 'en-GB'
  // rather than bare 'en' (which ICU resolves to en-US). See resolveIntlLocale.
  return i18n.global.d(
    value,
    { ...formatOptions, ...extraOptions },
    // vue-i18n types this param as a *registered* locale, but at runtime it
    // passes straight to Intl and accepts any BCP-47 tag once explicit
    // options are supplied (verified against 'en-GB'). The cast asserts
    // that, rather than registering a display-only locale bundle.
    resolveIntlLocale(contentLocale) as TLabAdminLocale
  )
}

/**
 * Fetch latest i18n version and load general messages for all locales in LAB_ADMIN_LOCALES.
 * Uses remote-only messages (no local/test overlays). Falls back silently on error.
 * When @team-milva/i18n-keys is locally linked, loads from the virtual module (raw JSON files)
 * instead of the CDN — no env var needed.
 */
export async function loadRemoteI18n(): Promise<void> {
  try {
    // Deliberately the BUILD mode, not ENV.isDev: the local bundle only exists when
    // @team-milva/i18n-keys is locally linked (package.links.json), which is a
    // property of how this bundle was built, not of the environment it runs in.
    // Gating on ENV.isDev would make stage and dev deploys fetch this whole
    // translations chunk at startup only to find no marker and fall through to the CDN.
    if (import.meta.env.DEV) {
      try {
        const mod = await import('@team-milva/i18n-keys/latest-translations')
        if ((mod as { __isLocalBundle__?: boolean }).__isLocalBundle__) {
          const { latestTranslations } = mod
          const localGeneral = latestTranslations.translations.general ?? {}
          const localLabAdmin =
            latestTranslations.translations['lab-admin'] ?? {}
          for (const locale of LAB_ADMIN_LOCALES) {
            const merged = merge(
              {},
              (localGeneral[locale] ?? {}) as Record<string, unknown>,
              (localLabAdmin[locale] ?? {}) as Record<string, unknown>
            ) as Record<string, unknown>
            i18n.global.setLocaleMessage(locale, merged)
          }
          return
        }
      } catch {
        // local bundle not available — fall through to CDN
      }
    }

    // Resolved lazily — HOST.translations is populated by initEnvironment()
    const i18nHost =
      (import.meta.env.VITE_I18N_HOST as string | undefined) ??
      (ENV.isLocalhost ? '' : HOST.translations)

    const latestRes = await fetch(`${i18nHost}/i18n/latest.json`, {
      cache: 'no-store',
    })
    if (!latestRes.ok) throw new Error(`latest.json ${latestRes.status}`)
    const latest = await latestRes.json()
    const version: string =
      typeof latest === 'string'
        ? latest
        : typeof latest?.version === 'string'
          ? latest.version
          : ''
    if (!version) throw new Error('Missing version in latest.json')

    const localeResults = await Promise.allSettled(
      LAB_ADMIN_LOCALES.map(async (locale) => {
        const [generalRes, labAdminRes] = await Promise.all([
          fetch(`${i18nHost}/i18n/${version}/general/${locale}.json`),
          fetch(`${i18nHost}/i18n/${version}/lab-admin/${locale}.json`),
        ])
        if (!generalRes.ok || !labAdminRes.ok)
          throw new Error('Failed locale fetch')
        const [general, labAdmin] = await Promise.all([
          generalRes.json(),
          labAdminRes.json(),
        ])
        return {
          locale,
          merged: merge({}, general, labAdmin) as Record<string, unknown>,
        }
      })
    )

    for (const [i, result] of localeResults.entries()) {
      if (result.status === 'fulfilled') {
        i18n.global.setLocaleMessage(result.value.locale, result.value.merged)
      } else if (ENV.isDev) {
        console.warn(
          `[i18n] Failed to load locale '${LAB_ADMIN_LOCALES[i]}':`,
          result.reason
        )
      }
    }
  } catch (err) {
    if (ENV.isDev) {
      console.warn('[i18n] Remote/local load skipped:', err)
    }
    // Fallback: keep existing local messages
  }
}

